/* LIBRAIRIRES */
import React, { useState, useEffect } from "react";

/* MUI */
import { Button, IconButton, TextField  } from "@mui/material";
import { Upload as UploadIcon, Folder as FolderIcon } from "@mui/icons-material";
import InputAdornment from '@mui/material/InputAdornment';

/* COMPONENT */
import CustomAlert from '../CustomAlert/CustomAlert';

/* ASSETS */
import CircularProgressWithLabel from '../../Assets/CircularProgress/CircularProgressWithLabel';

/* FIREBASE */
import { getStorage, ref, uploadBytes, uploadBytesResumable } from "firebase/storage";
import { doc, getDoc } from "firebase/firestore";
import { firestore } from "../../firebase.js";

/* ICONS */
import { RiInformationFill } from 'react-icons/ri';
import DescriptionIcon from '@mui/icons-material/Description';

/* CSS */
import './UploadFile.css';

function UploadFile(props) {
    const [file, setFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [userDetails, setUserDetails] = useState(null);
    const [openAlert, setOpenAlert] = useState(false);
    const [alertSeverity, setAlertSeverity] = useState("success");
    const [alertMessage, setAlertMessage] = useState("");
    const [documentTitle, setDocumentTitle] = useState("");

    const storage = getStorage();
    const userId = props.userId; // ID de l'utilisateur connecté

    useEffect(() => {
        // Récupère les informations de l'utilisateur depuis Firestore
        const userDocRef = doc(firestore, "users", userId);
        getDoc(userDocRef)
            .then((doc) => {
                if (doc.exists()) {
                    const data = doc.data();
                    setUserDetails(data);
                } else {
                    console.log("No such document!");
                }
            })
            .catch((error) => {
                console.log("Error getting document:", error);
            });
    }, [userId]);

    const handleFileChange = (event) => {
        setFile(event.target.files[0]);
    };

    const handleUploadClick = () => {
        if (!documentTitle) {
            setAlertSeverity("error");
            setAlertMessage("Veuillez saisir un titre pour le document.");
            setOpenAlert(true);
            return;
        };

        // Validation contre les injections SQL
        const sqlRegex = /[\0\x08\x09\x1a\n\r"'\\\%]/g;
        if (sqlRegex.test(documentTitle)) {
            setAlertSeverity("error");
            setAlertMessage("Le titre du document contient des caractères non autorisés.");
            setOpenAlert(true);
            return;
        }

        if (file) {
            setIsUploading(true);

            const metadata = {
                customMetadata: {
                    firstName: userDetails.firstName,
                    lastName: userDetails.name,
                    title: documentTitle
                },
            };

            // Vérifie si le fichier est une image ou un pdf
            if (file.type !== "image/jpeg" && file.type !== "image/png" && file.type !== "application/pdf") {
                setAlertSeverity("error");
                setAlertMessage("Le fichier doit être une image (jpeg ou png) ou un PDF.");
                setIsUploading(false);
                setOpenAlert(true);
                return;
            }

            // Génère un ID unique pour le fichier
            const fileId = Date.now().toString();

            // Crée une référence au fichier dans Firebase Storage
            const storageRef = ref(storage, `medias/${userId}/${fileId}`);
          
            // Upload le fichier en utilisant uploadBytesResumable
            const uploadTask = uploadBytesResumable(storageRef, file, metadata);

            // Surveille l'état de progression de l'upload
            uploadTask.on("state_changed", (snapshot) => {
                const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                setUploadProgress(progress);
            });

            // Envoie une notification lorsque l'upload est terminé
            uploadTask
            .then(() => {
                setAlertSeverity("success");
                setAlertMessage("Le fichier a été téléchargé avec succès !");
                setOpenAlert(true);
                console.log("File uploaded successfully.");
            })
            .catch((error) => {
                console.error("Error uploading file: ", error);
                setAlertSeverity("error");
                setAlertMessage("Erreur lors du téléchargement du fichier. Veuillez réessayer.");
                setOpenAlert(true);
            })
            .finally(() => {
                setIsUploading(false);
                setUploadProgress(0);
                setFile(null);
            });
        }
    };

    const handleDrop = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setFile(event.dataTransfer.files[0]);
    };

    const handleDragOver = (event) => {
        event.preventDefault();
        event.stopPropagation();
    };

    const handleCloseAlert = () => {
        setOpenAlert(false);
    }

    return (
        <div className='container-drag' style={{position: 'relative'}}>
            <div className='container-info'>
                <RiInformationFill size={30} style={{color: 'var(--primary)'}}/>
                <p style={{color: '#454545'}}>Veuillez déposer un fichier (image ou pdf)</p>
            </div> 
            <div className='dropzone height-drop' onDrop={handleDrop} onDragOver={handleDragOver} style={{ border: "2px dashed #aaa", padding: "1rem", borderRadius: "4px", margin: "1rem 0", width: '100%', height: 'calc(100% - 85px)' }}>   
                <input type="file" onChange={handleFileChange} style={{ display: "none", marginTop: "30px" }} />
                <Button onClick={() => document.querySelector("input[type='file']").click()} variant="contained" startIcon={<FolderIcon />} style={{ marginRight: "0.5rem" }}>
                    Parcourir
                </Button>
                <IconButton onClick={handleUploadClick} disabled={!file || isUploading} color="primary" aria-label="upload">
                    <UploadIcon />
                </IconButton>
                <TextField
                    label="Titre du document"
                    value={documentTitle}
                    onChange={(event) => setDocumentTitle(event.target.value)}
                    style={{ marginRight: "0.5rem", width: '50%', marginTop: '5px', marginLeft: "50px" }}
                    InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <DescriptionIcon />
                          </InputAdornment>
                        ),
                        style: {
                            height: "40px",
                        },
                      }}
                />
                <CustomAlert 
                    open={openAlert}
                    onClose={handleCloseAlert}
                    message={alertMessage}
                    severity={alertSeverity}
                />
            </div>
            { isUploading ? 
                <div className="progress-bar">
                    <CircularProgressWithLabel value={uploadProgress} /> 
                </div>
                :
                <span className="plus-icon">+</span>
            }
        </div>
    );
}

export default UploadFile;
